import type { MouseEvent, ReactNode, Ref } from "react";
import React from "react";

import Link from "~/atoms/Link";
import { FRONTEND_URL } from "~/constants/all";

/**
 * Interface for InnerButtonProps
 * @interface
 * @property {ReactNode} children - The content to be rendered within the button.
 * @property {ReactNode} endAdornment - The adornment at the end of the button.
 * @property {ReactNode} startAdornment - The adornment at the start of the button.
 */
interface InnerButtonProps {
  children: ReactNode;
  endAdornment?: ReactNode;
  startAdornment?: ReactNode;
}

const InnerButton: React.FC<InnerButtonProps> = ({
  children,
  endAdornment,
  startAdornment,
}) => (
  <>
    {startAdornment && <div className="-ml-0.5 h-4 w-4">{startAdornment}</div>}
    {children}
    {endAdornment && <div className="-mr-0.5 h-4 w-4">{endAdornment}</div>}
  </>
);

/**
 * Types for ButtonProps
 * @typedef {string} ButtonColor - The color of the button. Can be 'primary', 'secondary', 'danger', 'tertiary'.
 * @typedef {string} ButtonSize - The size of the button. Can be 'xs', 'sm', 'md', 'lg', 'xl'.
 * @typedef {string} ButtonType - The type of the button. Can be 'button', 'submit'.
 * @typedef {string} ButtonVariant - The variant of the button. Can be 'solid', 'outlined', 'ghost'.
 */

export type ButtonColor = "primary" | "secondary" | "danger" | "tertiary";
export type ButtonSize = "xs" | "sm" | "md" | "lg" | "xl";
export type ButtonType = "button" | "submit";
export type ButtonVariant = "solid" | "outlined" | "ghost";

/**
 * Detects which color types are present in the custom styles
 * Returns an object indicating which color types should be overridden
 */
const detectColorOverrides = (
  customStyles: string
): Record<string, boolean> => {
  return {
    bg: /\bbg-/.test(customStyles),
    text: /\btext-/.test(customStyles),
    ring: /\bring-/.test(customStyles),
    border: /\bborder-/.test(customStyles),
    outline: /\boutline-/.test(customStyles),
  };
};

/**
 * Filters out color classes from preset styles based on detected overrides
 * Removes all related classes including variants (hover:, focus:, disabled:, etc.)
 */
const applyColorOverrides = (
  presetStyles: string,
  customStyles: string
): string => {
  const overrides = detectColorOverrides(customStyles);

  if (!Object.values(overrides).some((v) => v)) {
    // No color overrides detected, return original styles
    return presetStyles;
  }

  return presetStyles
    .split(" ")
    .filter((className) => {
      // Check each color type and filter if override is detected
      if (overrides.bg && /\bbg-/.test(className)) return false;
      if (overrides.text && /\btext-/.test(className)) return false;
      if (overrides.ring && /\bring-/.test(className)) return false;
      if (overrides.border && /\bborder-/.test(className)) return false;
      if (overrides.outline && /\boutline-/.test(className)) return false;

      return true;
    })
    .join(" ");
};

const variantOptions = {
  primary: {
    solid:
      "bg-blue-600 hover:bg-blue-500 text-white focus-visible:outline-blue-600 disabled:text-white",
    outlined:
      "bg-white text-blue-600 ring-1 ring-inset ring-blue-600 hover:bg-gray-50 disabled:text-gray-400 disabled:ring-gray-300 disabled:bg-white",
    ghost:
      "bg-transparent hover:bg-gray-50 text-blue-600 shadow-none focus-visible:outline-transparent disabled:text-gray-500",
  },
  secondary: {
    solid:
      "bg-fuchsia-600 hover:bg-fuchsia-500 text-white focus-visible:outline-fuchsia-600 disabled:text-white",
    outlined:
      "bg-white text-fuchsia-600 ring-1 ring-inset ring-fuchsia-600 hover:bg-gray-50 disabled:text-gray-400 disabled:ring-gray-300 disabled:bg-white",
    ghost:
      "bg-transparent hover:bg-gray-50 text-fuchsia-600 shadow-none focus-visible:outline-transparent disabled:text-gray-500",
  },
  danger: {
    solid:
      "bg-red-600 hover:bg-red-500 text-white focus-visible:outline-red-600 disabled:text-white",
    outlined:
      "bg-white text-red-600 ring-1 ring-inset ring-red-600 hover:bg-gray-50 disabled:text-gray-400 disabled:ring-gray-300 disabled:bg-white",
    ghost:
      "bg-transparent hover:bg-gray-50 text-red-600 shadow-none focus-visible:outline-transparent disabled:text-gray-500",
  },
  tertiary: {
    solid:
      "bg-slate-600 hover:bg-slate-500 text-white focus-visible:outline-slate-600 disabled:text-white",
    outlined:
      "bg-white text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-gray-50 disabled:text-gray-400 disabled:ring-gray-300 disabled:bg-white",
    ghost:
      "bg-transparent hover:bg-gray-50 text-slate-600 shadow-none focus-visible:outline-transparent disabled:text-gray-500",
  },
};

const buttonSizes = {
  xs: "rounded-md px-2 py-1 text-xs",
  sm: "rounded-md px-3 py-2 text-sm",
  md: "rounded-md px-4 py-3 text-sm",
  lg: "rounded-md px-4 py-4 text-sm",
  xl: "rounded-md px-4 py-5 text-sm",
};

/**
 * Interface for ButtonProps
 * @interface
 * @property {ReactNode} children - The content to be rendered within the button.
 * @property {ButtonColor} color - The color of the button.
 * @property {string} customStyles - Any custom styles to be applied to the button.
 * @property {boolean} disabled - Whether the button is disabled.
 * @property {ReactNode} endAdornment - The adornment at the end of the button.
 * @property {boolean} fullWidth - Whether the button should take up the full width of its container.
 * @property {string} href - The URL to navigate to when the button is clicked.
 * @property {(event: MouseEvent<HTMLButtonElement>) => void} onClick - The function to call when the button is clicked.
 * @property {ButtonSize} size - The size of the button.
 * @property {ReactNode} startAdornment - The adornment at the start of the button.
 * @property {ButtonType} type - The type of the button.
 * @property {ButtonVariant} variant - The variant of the button.
 */
export interface ButtonProps {
  children: ReactNode;
  color?: ButtonColor;
  customStyles?: string;
  disabled?: boolean;
  download?: boolean;
  endAdornment?: ReactNode;
  fullWidth?: boolean;
  href?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  ref?: Ref<HTMLButtonElement>;
  size?: ButtonSize;
  startAdornment?: ReactNode;
  type?: ButtonType;
  variant?: ButtonVariant;
}

function Button({
  children,
  color = "primary",
  customStyles = "",
  disabled = false,
  download = false,
  endAdornment = null,
  fullWidth = false,
  href = "",
  onClick = undefined,
  ref,
  size = "md",
  startAdornment = null,
  type = "button",
  variant = "solid",
  ...rest
}: ButtonProps) {
  const rootClasses =
    "font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:bg-gray-300 flex justify-center duration-200 ease-in-out" +
    (fullWidth ? " w-full" : "");

  const buttonVariants = {
    solid: variantOptions[color].solid,
    outlined: variantOptions[color].outlined,
    ghost: variantOptions[color].ghost,
  };

  const isGhost = variant === "ghost";
  const frontendUrlMatch = new RegExp(FRONTEND_URL || "");
  const isExternalLink = /http/.test(href) && !href.match(frontendUrlMatch);

  // Apply color overrides to variant styles
  const getVariantStyles = () => {
    return applyColorOverrides(buttonVariants[variant], customStyles);
  };

  if (href !== "") {
    if (isExternalLink || download) {
      // If the link is external, open it in a new tab
      return (
        <a
          href={href}
          target={isExternalLink ? "_blank" : "_self"}
          rel={isExternalLink ? "noreferrer noopener" : undefined}
          className={`${rootClasses} ${buttonSizes[size]} ${getVariantStyles()} ${
            startAdornment || endAdornment
              ? "inline-flex items-center gap-x-1.5"
              : ""
          } ${!isGhost && "shadow-xs"} ${customStyles}`}
          download={download}
        >
          <InnerButton
            endAdornment={endAdornment}
            startAdornment={startAdornment}
          >
            {children}
          </InnerButton>
        </a>
      );
    }

    return (
      <Link
        href={href}
        className={`${rootClasses} ${buttonSizes[size]} ${getVariantStyles()} ${
          startAdornment || endAdornment
            ? "inline-flex items-center gap-x-1.5"
            : ""
        } ${!isGhost && "shadow-xs"} ${customStyles}`}
        {...rest}
      >
        <InnerButton
          endAdornment={endAdornment}
          startAdornment={startAdornment}
        >
          {children}
        </InnerButton>
      </Link>
    );
  }

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={`${rootClasses} ${buttonSizes[size]} ${getVariantStyles()} ${
        startAdornment || endAdornment
          ? "inline-flex items-center gap-x-1.5"
          : ""
      } ${!isGhost && "shadow-xs"} ${customStyles}`}
      ref={ref}
      {...rest}
    >
      <InnerButton endAdornment={endAdornment} startAdornment={startAdornment}>
        {children}
      </InnerButton>
    </button>
  );
}

export default Button;
